import React, { useState, useEffect, useContext, useRef } from 'react';
import { observer } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert, messageRegExp } from 'services/utils';
import { MAX_DOCUMENT_COMMENT_LENGTH } from 'components/common/constants';
import { documentRequestTokenApi } from 'services/documentRequestApi';
import Button from 'uikit/Button/Button';
import Alert from 'uikit/Alert/Alert';
import Input from 'uikit/Input/Input';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import Footer from 'components/common/Footer';
import Loader from 'components/common/Loader';
import DocumentRequestHeader from './DocumentRequestHeader';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';

// Import document icons
import { ReactComponent as InvoiceIcon } from 'assets/documentRequestIcons/invoice.svg';
import { ReactComponent as AgreementIcon } from 'assets/documentRequestIcons/agreement.svg';
import { ReactComponent as BankStatementIcon } from 'assets/documentRequestIcons/bank-statement.svg';
import { ReactComponent as IdentificationIcon } from 'assets/documentRequestIcons/identification-document-of-counterparty.svg';
import { ReactComponent as ProofOfAddressIcon } from 'assets/documentRequestIcons/proof-of-aaddress-of-counterparty.svg';
import { ReactComponent as ProofOfDividendsIcon } from 'assets/documentRequestIcons/proof-of-dividends.svg';
import { ReactComponent as ProofOfWorkIcon } from 'assets/documentRequestIcons/proof-of-work.svg';
import { ReactComponent as PurposeOfPaymentIcon } from 'assets/documentRequestIcons/purpose-of-payment.svg';
import { ReactComponent as TaxDeclarationIcon } from 'assets/documentRequestIcons/tax-declaration.svg';
import { ReactComponent as TransportationIcon } from 'assets/documentRequestIcons/transportation-documents.svg';
import { ReactComponent as OtherIcon } from 'assets/documentRequestIcons/other.svg';

import { DOCUMENT_ITEM_TYPES } from 'stores/documentRequestStore';
import './styles.scss';

const DocumentUploadStep = ({
  documentRequestStore,
  userStore,
  isAuthorizedFlow,
  item,
  currentStep,
  totalSteps,
  onBack,
  onContinue
}) => {
  const i18n = useContext(i18nContext);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoadingExistingFiles, setIsLoadingExistingFiles] = useState(false);
  const [comment, setComment] = useState(String(item?.comment || ''));
  const [commentError, setCommentError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const logoPath = process.env.REACT_APP_LOGO_PATH;
  const fileIdCounter = useRef(0);

  // Load existing files from server as blobs
  useEffect(() => {
    const loadExistingFiles = async () => {
      // Only load if there are existing files
      if (!item?.files || item.files.length === 0) {
        return;
      }

      // Don't reload files if the item has already been submitted
      // When returning via Edit button, submittedAt will be set
      if (item.submittedAt !== null) {
        // Just set the files metadata for display without downloading
        setUploadedFiles(
          item.files.map((file) => ({
            ...file,
            id: file.id,
            name: file.name || 'Unknown file',
            size: file.size || 0,
            isExistingFile: true,
            serverId: file.id,
            // Mark as already on server so we don't re-upload
            alreadyOnServer: true
          }))
        );
        return;
      }
      setIsLoadingExistingFiles(true);

      try {
        const loadedFiles = [];

        for (const existingFile of item.files) {
          if (existingFile.id) {
            try {
              // Download the file from server
              const customerNumber = userStore?.userData?.account?.account_number;
              const response =
                isAuthorizedFlow && customerNumber
                  ? await documentRequestStore.downloadDocument(customerNumber, existingFile.id)
                  : await documentRequestTokenApi.downloadDocument(
                      documentRequestStore.requestId,
                      existingFile.id,
                      documentRequestStore.token
                    );

              // Convert response to blob if it's not already
              let blob;
              if (response.body instanceof Blob) {
                blob = response.body;
              } else if (response instanceof Blob) {
                blob = response;
              } else {
                console.error('Unexpected response format for file download');
                continue;
              }

              // Create a File object from the blob
              const file = new File([blob], existingFile.name || 'file', {
                type: blob.type || 'application/octet-stream'
              });

              // Preserve the original file ID for tracking
              file.serverId = existingFile.id;
              file.id = existingFile.id; // Keep the same ID for UI consistency
              file.isExistingFile = true;

              loadedFiles.push(file);
            } catch (error) {
              console.error(`Failed to load existing file ${existingFile.id}:`, error);
              // If we can't load the file, keep the metadata for display
              loadedFiles.push({
                ...existingFile,
                id: existingFile.id,
                name: existingFile.name || 'Unknown file',
                size: existingFile.size || 0,
                isExistingFile: true,
                loadError: true
              });
            }
          }
        }

        setUploadedFiles(loadedFiles);
      } catch (error) {
        console.error('Failed to load existing files:', error);
        // Fallback to metadata only
        if (item?.files && Array.isArray(item.files)) {
          setUploadedFiles(
            item.files.map((file) => ({
              ...file,
              id: file.id,
              name: file.name || 'Unknown file',
              size: file.size || 0,
              isExistingFile: true,
              loadError: true
            }))
          );
        }
      } finally {
        setIsLoadingExistingFiles(false);
      }
    };

    loadExistingFiles();
  }, [item, documentRequestStore.requestId, documentRequestStore.token]);

  // Get icon for document type
  const getDocumentIcon = (type) => {
    const iconMap = {
      [DOCUMENT_ITEM_TYPES.INVOICE]: InvoiceIcon,
      [DOCUMENT_ITEM_TYPES.AGREEMENT]: AgreementIcon,
      [DOCUMENT_ITEM_TYPES.BANK_STATEMENT]: BankStatementIcon,
      [DOCUMENT_ITEM_TYPES.IDENTIFICATION_DOCUMENT]: IdentificationIcon,
      [DOCUMENT_ITEM_TYPES.PROOF_OF_ADDRESS]: ProofOfAddressIcon,
      [DOCUMENT_ITEM_TYPES.PROOF_OF_DIVIDENDS]: ProofOfDividendsIcon,
      [DOCUMENT_ITEM_TYPES.PROOF_OF_WORK]: ProofOfWorkIcon,
      [DOCUMENT_ITEM_TYPES.PURPOSE_OF_PAYMENT]: PurposeOfPaymentIcon,
      [DOCUMENT_ITEM_TYPES.TAX_DECLARATION]: TaxDeclarationIcon,
      [DOCUMENT_ITEM_TYPES.TRANSPORTATION_DOCUMENTS]: TransportationIcon,
      [DOCUMENT_ITEM_TYPES.OTHER]: OtherIcon
    };

    const Icon = iconMap[type] || OtherIcon;
    return <Icon className='step-icon' />;
  };

  // Get document display name
  const getDocumentDisplayName = (item) => {
    if (item.type === DOCUMENT_ITEM_TYPES.OTHER && item.name) {
      return item.name;
    }

    const typeKeys = {
      [DOCUMENT_ITEM_TYPES.INVOICE]: 'documentRequest.types.invoice',
      [DOCUMENT_ITEM_TYPES.AGREEMENT]: 'documentRequest.types.agreement',
      [DOCUMENT_ITEM_TYPES.TRANSPORTATION_DOCUMENTS]: 'documentRequest.types.transportationDocuments',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_WORK]: 'documentRequest.types.proofOfWork',
      [DOCUMENT_ITEM_TYPES.BANK_STATEMENT]: 'documentRequest.types.bankStatement',
      [DOCUMENT_ITEM_TYPES.TAX_DECLARATION]: 'documentRequest.types.taxDeclaration',
      [DOCUMENT_ITEM_TYPES.IDENTIFICATION_DOCUMENT]: 'documentRequest.types.identificationDocument',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_ADDRESS]: 'documentRequest.types.proofOfAddress',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_DIVIDENDS]: 'documentRequest.types.proofOfDividends',
      [DOCUMENT_ITEM_TYPES.PURPOSE_OF_PAYMENT]: 'documentRequest.types.purposeOfPayment',
      [DOCUMENT_ITEM_TYPES.OTHER]: 'documentRequest.types.other'
    };

    return i18n.getMessage(typeKeys[item.type]) || item.type;
  };

  // Get document description from i18n
  const getDocumentDescription = (type) => {
    const typeMap = {
      [DOCUMENT_ITEM_TYPES.INVOICE]: 'invoice',
      [DOCUMENT_ITEM_TYPES.AGREEMENT]: 'agreement',
      [DOCUMENT_ITEM_TYPES.TRANSPORTATION_DOCUMENTS]: 'transportationDocuments',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_WORK]: 'proofOfWork',
      [DOCUMENT_ITEM_TYPES.BANK_STATEMENT]: 'bankStatement',
      [DOCUMENT_ITEM_TYPES.TAX_DECLARATION]: 'taxDeclaration',
      [DOCUMENT_ITEM_TYPES.IDENTIFICATION_DOCUMENT]: 'identificationDocument',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_ADDRESS]: 'proofOfAddress',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_DIVIDENDS]: 'proofOfDividends',
      [DOCUMENT_ITEM_TYPES.PURPOSE_OF_PAYMENT]: 'purposeOfPayment',
      [DOCUMENT_ITEM_TYPES.OTHER]: 'other'
    };

    const key = typeMap[type] || 'other';
    return {
      main: i18n.getMessage(`documentRequest.descriptions.${key}.main`),
      doNotAccept: i18n.getMessage(`documentRequest.descriptions.${key}.doNotAccept`)
    };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // TODO: Add toast notification
  };

  const handleFilesChange = (event) => {
    // Handle the event structure from CustomFileDragAndDrop
    if (event && event.target && event.target.files) {
      const newFiles = Array.from(event.target.files).map((file) => {
        // Add an id to each file for tracking
        return Object.assign(file, {
          id: `file-${fileIdCounter.current++}`
        });
      });
      setUploadedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
  };

  const handleCommentChange = (e) => {
    let value = '';

    // Если это объект события, берем значение из target
    if (e && typeof e === 'object' && 'target' in e) {
      value = e.target.value || '';
    } else if (typeof e === 'string') {
      // Если это строка, используем напрямую
      value = e;
    }

    setComment(value);

    // Validate comment
    if (value && !messageRegExp.test(value)) {
      setCommentError(i18n.getMessage('messages.error.message.invalidFormat') || 'Cyrillic characters are not allowed');
    } else if (value.length > MAX_DOCUMENT_COMMENT_LENGTH) {
      setCommentError(
        i18n.getMessage('messages.error.message.maxSize', { size: MAX_DOCUMENT_COMMENT_LENGTH }) ||
          `Maximum ${MAX_DOCUMENT_COMMENT_LENGTH} characters allowed`
      );
    } else {
      setCommentError('');
    }
  };

  const handleContinue = async () => {
    if (!uploadedFiles.length && !comment.trim()) {
      return; // Button should be disabled anyway
    }

    // Don't submit if there's a comment validation error
    if (commentError) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Separate files that are already on server from new/reloaded files
      const filesToSubmit = uploadedFiles.filter((file) => {
        // Don't submit files that are already on server (from Edit return)
        if (file.alreadyOnServer) {
          return false;
        }
        // Submit File objects that don't have load errors
        return file instanceof File && !file.loadError;
      });

      // Get IDs of files that were deleted (if we had existing files)
      const deletedDocumentIds = [];
      if (item?.files && Array.isArray(item.files)) {
        // Check which original files are still present
        const currentServerIds = uploadedFiles
          .filter((f) => f.serverId || (f.id && f.isExistingFile))
          .map((f) => f.serverId || f.id);

        item.files.forEach((existingFile) => {
          if (existingFile.id && !currentServerIds.includes(existingFile.id)) {
            deletedDocumentIds.push(existingFile.id);
          }
        });
      }

      // Only submit if we have new files or just updating comment
      // If item.submittedAt is set, we're just updating the comment
      const customerNumber = userStore?.userData?.account?.account_number;

      if (filesToSubmit.length > 0 || item.submittedAt !== null) {
        if (isAuthorizedFlow && customerNumber) {
          await documentRequestStore.submitItem(customerNumber, item.type, filesToSubmit, comment, deletedDocumentIds);
        } else {
          await documentRequestStore.submitItemByToken(item.type, filesToSubmit, comment, deletedDocumentIds);
        }
      } else if (!uploadedFiles.length) {
        // No files at all, just submit with comment
        if (isAuthorizedFlow && customerNumber) {
          await documentRequestStore.submitItem(customerNumber, item.type, [], comment, deletedDocumentIds);
        } else {
          await documentRequestStore.submitItemByToken(item.type, [], comment, deletedDocumentIds);
        }
      }
      onContinue();
    } catch (error) {
      console.error('Failed to submit item:', error);
      const errorMessage = getErrorMessageForAlert(i18n, error, true, null, null);
      setSubmitError(
        errorMessage ||
          error?.response?.data?.message ||
          error?.message ||
          i18n.getMessage('documentRequest.error.submitFailed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled = (!uploadedFiles.length && !comment.trim()) || !!commentError;
  const description = getDocumentDescription(item.type);

  return (
    <div className='document-request-container'>
      <div className='document-request-header'>
        <a href={process.env.REACT_APP_LINK_HOME_PAGE} className='logo'>
          <img src={logoPath} alt='Logo' className='icon-logo' />
        </a>
      </div>

      <div className='document-upload-content'>
        {/* Content Header */}
        <div className='content-header'>
          <h1>{i18n.getMessage('documentRequest.title')}</h1>

          {/* Deadline and Transaction Info */}
          <DocumentRequestHeader
            expiresAt={documentRequestStore.expiresAt}
            transactionId={documentRequestStore.currentRequest?.transactionNumber || null}
            showDeadline={true}
            showTransaction={true}
          />
        </div>

        {/* Transaction Details Section */}
        <div className='transaction-details-section'>
          <div className='transaction-details-content'>
            {/* Counterparty */}
            {documentRequestStore.currentRequest?.partyName && (
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('documentRequest.counterparty')}</span>
                <span className='detail-value'>{documentRequestStore.currentRequest.partyName}</span>
              </div>
            )}
            {/* Amount */}
            {documentRequestStore.currentRequest?.amountDetails?.displayAmount && (
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('documentRequest.amount')}</span>
                <span className='detail-value'>{documentRequestStore.currentRequest.amountDetails.displayAmount}</span>
              </div>
            )}
            {/* Reference */}
            {documentRequestStore.currentRequest?.comment && (
              <div className='detail-row'>
                <span className='detail-label'>{i18n.getMessage('documentRequest.reference')}</span>
                <div className='detail-value-with-action'>
                  <span className='detail-value'>{documentRequestStore.currentRequest.comment}</span>
                  <button
                    className='copy-button'
                    onClick={() => copyToClipboard(documentRequestStore.currentRequest.comment)}
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Document Step Header */}
        <div className='document-step-header'>
          <div className='step-icon-container'>{getDocumentIcon(item.type)}</div>
          <h2 className='step-title'>
            <span className='document-name'>{getDocumentDisplayName(item)}</span>
            <span className='step-counter'>
              {' '}
              - {currentStep}/{totalSteps}
            </span>
          </h2>
        </div>

        {/* Document Description */}
        <div className='document-description'>
          <p className='description-main'>{description.main}</p>
          {description.doNotAccept && (
            <p className='description-warning'>
              <strong>{i18n.getMessage('documentRequest.doNotAccept')}</strong> {description.doNotAccept}
            </p>
          )}
        </div>

        {/* File Upload */}
        <div className='upload-section'>
          {isLoadingExistingFiles ? (
            <div className='loading-files'>
              <Loader />
              <p>{i18n.getMessage('documentRequest.loadingExistingFiles') || 'Loading existing files...'}</p>
            </div>
          ) : (
            <CustomFileDragAndDrop
              files={uploadedFiles}
              onChange={handleFilesChange}
              onRemoveFiles={handleRemoveFile}
              accept='.pdf,.jpg,.jpeg,.png'
              maxSize={15 * 1024 * 1024} // 15MB in bytes
              multiple={true}
            />
          )}
        </div>

        {/* Comment Input */}
        <div className='comment-section'>
          <Input
            type={'textarea'}
            className={'document-request-comment-input'}
            name={'comment'}
            label={i18n.getMessage('documentRequest.addComment')}
            placeholder={i18n.getMessage('documentRequest.commentPlaceholder')}
            value={comment}
            onChange={handleCommentChange}
            rows={3}
            max={MAX_DOCUMENT_COMMENT_LENGTH}
            error={commentError}
            symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
              amount: MAX_DOCUMENT_COMMENT_LENGTH - comment.length
            })}
          />
        </div>

        {/* Error Alert */}
        {submitError && <Alert type='warning' message={submitError} />}

        {/* Action Buttons */}
        <div className='action-buttons-document-upload'>
          <Button type='outline' onClick={onBack} isDisabled={isSubmitting || isLoadingExistingFiles}>
            {i18n.getMessage('documentRequest.back')}
          </Button>
          <Button
            type='primary'
            onClick={handleContinue}
            isDisabled={isButtonDisabled || isSubmitting || isLoadingExistingFiles}
            className='continue-button'
          >
            {isSubmitting ? <Loader /> : i18n.getMessage('documentRequest.continue')}
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default observer(DocumentUploadStep);
